import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

/**
 * Camera service for capturing photos using Capacitor Camera plugin
 */

/**
 * Captures a photo using the device camera and returns it as a File object
 * @param quality - Photo quality (0-100), defaults to 90
 * @returns Promise that resolves to a File object or null if cancelled
 */
export async function capturePhoto(quality: number = 90): Promise<File | null> {
  try {
    const image = await Camera.getPhoto({
      quality: quality,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: CameraSource.Camera, // Open camera directly
    });

    if (!image.base64String) {
      console.error('No base64 data received from camera');
      return null;
    }

    // Convert base64 to blob
    const base64Data = image.base64String;
    const mimeType = `image/${image.format || 'jpeg'}`;
    const blob = base64ToBlob(base64Data, mimeType);

    // Create File object with timestamp-based name
    const timestamp = Date.now();
    const fileName = `camera-photo-${timestamp}.${image.format || 'jpg'}`;
    const file = new File([blob], fileName, { type: mimeType });

    return file;
  } catch (error) {
    // User cancelled or permission denied
    if (error instanceof Error && error.message.includes('User cancelled')) {
      console.log('Camera capture cancelled by user');
      return null;
    }
    
    console.error('Error capturing photo:', error);
    throw error;
  }
}

/**
 * Converts base64 string to Blob
 * @param base64 - Base64 encoded string
 * @param mimeType - MIME type of the image
 * @returns Blob object
 */
function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteArrays: Uint8Array[] = [];

  // Process in chunks for better performance with large images
  const sliceSize = 512;
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
}

/**
 * Checks if camera is available on the device
 * @returns Promise that resolves to true if camera is available
 */
export async function isCameraAvailable(): Promise<boolean> {
  try {
    // Try to check camera permissions
    const permissions = await Camera.checkPermissions();
    return permissions.camera !== 'denied';
  } catch (error) {
    console.error('Error checking camera availability:', error);
    return false;
  }
}

/**
 * Requests camera permissions from the user
 * @returns Promise that resolves to true if permissions granted
 */
export async function requestCameraPermissions(): Promise<boolean> {
  try {
    const permissions = await Camera.requestPermissions();
    return permissions.camera === 'granted';
  } catch (error) {
    console.error('Error requesting camera permissions:', error);
    return false;
  }
}
